export const POWER_ASSUMPTIONS = {
  phoneFullChargeKwh: 0.015,
  usHomeMonthlyKwh: 865,
  coalExampleNameplateMw: 500,
  usCoalCapacityFactor2024: 0.426,
} as const

export const infrastructureSources = [
  {
    label: 'Electricity units',
    publisher: 'U.S. Energy Information Administration',
    url: 'https://www.eia.gov/energyexplained/electricity/measuring-electricity.php',
    note: 'One MW equals one million watts; energy used over time is measured in watt-hours.',
  },
  {
    label: 'Data-center scale',
    publisher: 'International Energy Agency',
    url: 'https://www.iea.org/reports/energy-and-ai/understanding-the-energy-ai-nexus',
    note: 'Illustrative 2024 capacities: 25 MW conventional, 100 MW hyperscale AI, and about 2,000 MW for the largest under construction.',
  },
  {
    label: 'U.S. household use',
    publisher: 'U.S. Energy Information Administration',
    url: 'https://www.eia.gov/todayinenergy/detail.php?id=65244',
    note: 'Average grid-delivered residential use was 865 kWh per customer per month in 2024.',
  },
  {
    label: 'Coal capacity factor',
    publisher: 'U.S. Energy Information Administration',
    url: 'https://www.eia.gov/electricity/annual/table.php?t=epa_04_08_a.html',
    note: 'The U.S. utility-scale coal fleet had a 42.6% capacity factor in 2024.',
  },
  {
    label: 'U.S. data-center demand',
    publisher: 'U.S. Department of Energy',
    url: 'https://www.energy.gov/articles/doe-releases-new-report-evaluating-increase-electricity-demand-data-centers',
    note: 'Data centers used about 4.4% of U.S. electricity in 2023; DOE projected 6.7% to 12% by 2028.',
  },
] as const

export type PowerComparisons = {
  loadMw: number
  dailyEnergyMwh: number
  averageHouseholdsEquivalent: number
  phoneFullChargesPerHour: number
  coalAverageOutputShare: number
  coalAverageOutputMw: number
  coalAnnualEnergyMwh: number
  coalAnnualHouseholdsEquivalent: number
}

export function calculatePowerComparisons(loadMw: number): PowerComparisons {
  const safeLoadMw = Number.isFinite(loadMw) ? Math.max(0, loadMw) : 0
  const homeAnnualKwh = POWER_ASSUMPTIONS.usHomeMonthlyKwh * 12
  const homeAverageKw = homeAnnualKwh / 8_760
  const coalAverageOutputMw = POWER_ASSUMPTIONS.coalExampleNameplateMw * POWER_ASSUMPTIONS.usCoalCapacityFactor2024
  const coalAnnualEnergyMwh = coalAverageOutputMw * 8_760

  return {
    loadMw: safeLoadMw,
    dailyEnergyMwh: safeLoadMw * 24,
    averageHouseholdsEquivalent: safeLoadMw * 1_000 / homeAverageKw,
    phoneFullChargesPerHour: safeLoadMw * 1_000 / POWER_ASSUMPTIONS.phoneFullChargeKwh,
    coalAverageOutputShare: safeLoadMw / coalAverageOutputMw,
    coalAverageOutputMw,
    coalAnnualEnergyMwh,
    coalAnnualHouseholdsEquivalent: coalAnnualEnergyMwh / (homeAnnualKwh / 1_000),
  }
}
